import { VCardOption } from '@/lib/types/VCardT'
import { cn, getWordInitials } from '@/lib/utils'
import { onColor } from '@/lib/color/contrast'
import Image from '@/components/Image'
import LcIcon from '@/components/livecontact/icons'
import { OPTION_CLASS } from '@/components/livecontact/fieldStyles'

/** Card avatar: photo when real, otherwise initials on the card's own colour. */
export const VCardAvatar = ({
  vCard,
  size,
  className,
}: {
  vCard: Pick<VCardOption, 'representativeName' | 'avatar' | 'isAvatarGenerated' | 'mainColor'>
  size: number
  className?: string
}) => {
  const name = vCard.representativeName || 'Unknown'
  const hasAvatar = vCard.avatar && !vCard.isAvatarGenerated
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold',
        !hasAvatar && !vCard.mainColor && 'bg-brand text-brand-ink',
        className
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(9, Math.round(size * 0.38)),
        ...(!hasAvatar && vCard.mainColor
          ? { backgroundColor: vCard.mainColor, color: onColor(vCard.mainColor) }
          : {}),
      }}
    >
      {hasAvatar ? (
        <Image
          src={vCard.avatar}
          alt={name}
          width={size}
          height={size}
          className="size-full object-cover"
        />
      ) : (
        getWordInitials(name)
      )}
    </span>
  )
}

/** Option row: card avatar, name, check that pops in when selected. */
const VCardDropdownItem = ({
  vCard,
  isSelected,
  onSelect,
}: {
  vCard: VCardOption
  isSelected: boolean
  onSelect: (vCard: VCardOption) => void
}) => {
  const name = vCard.representativeName || 'Unknown'

  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => onSelect(vCard)}
      className={cn(
        OPTION_CLASS,
        'py-1.5',
        isSelected && 'bg-surface-2 text-ink dark:bg-surface-3'
      )}
    >
      <VCardAvatar vCard={vCard} size={28} />
      <span className="min-w-0 flex-1 truncate">{name}</span>
      <span className="inline-flex w-4 shrink-0 justify-center">
        {isSelected && (
          <LcIcon name="check" strokeWidth={2} className="text-brand animate-lc-pop" />
        )}
      </span>
    </button>
  )
}

export default VCardDropdownItem
